<div class="flex flex-col h-full">
  <!-- Header: Modul-Icon + Modulname -->
  <div class="flex items-center justify-between gap-4 px-4 py-2 border-b border-gray-200 min-h-[55px]">
    <div class="flex items-center gap-4 min-w-0">
      @if (moduleIcon) {
        <fa-icon [icon]="moduleIcon" class="text-gray-500 shrink-0" style="font-size:16px"></fa-icon>
      }
      <span class="text-base font-medium text-gray-900 truncate">{{ nodeName }}</span>
    </div>
    <button
      (click)="closed.emit()"
      class="flex items-center justify-center w-7 h-7 rounded hover:bg-gray-100 transition-colors shrink-0">
      <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
      </svg>
    </button>
  </div>

  <div class="flex-1 overflow-y-auto">
    <div class="flex flex-col gap-4 px-4 py-3">

      @if (hasCustomOrder) {
        <!-- Interleaved: Eingänge + Einstellungen nach x-order gemischt (z.B. Wert1, Operator, Wert2) -->
        @for (item of orderedPanelItems; track item.key) {
          @if (item.kind === 'input') {
            <ng-container [ngTemplateOutlet]="inputFieldTpl" [ngTemplateOutletContext]="{field: item.field}"></ng-container>
          } @else {
            <app-schema-field
              (valueChange)="onSettingsField({key: item.settingKey, value: $event})"
              [resetKey]="nodeId"
              [schema]="item.schema"
              [value]="settingValueAt(item.settingKey, item.schema)">
            </app-schema-field>
          }
        }
      } @else {
        <!-- Eingänge (inline, aus inputSchema + Graph-Kanten) -->
        @for (field of visibleInputFields; track field.portIndex) {
          <ng-container [ngTemplateOutlet]="inputFieldTpl" [ngTemplateOutletContext]="{field}"></ng-container>
        }

        <!-- Einstellungen (generisch aus dem Manifest-settingsSchema) -->
        @if (settingsSchema?.properties) {
          <app-schema-form
            (fieldChange)="onSettingsField($event)"
            [resetKey]="nodeId"
            [schema]="settingsSchema"
            [value]="settingsValues">
          </app-schema-form>
        }

        <!-- eingangsparameter im Datei-Modus (z.B. IFC): es wird nichts extrahiert, der Knoten
             reicht die Datei weiter. Der Parameter-Block waere hier irrefuehrend. -->
        @if (isEingangsparameterNode && isEingangDateiModus) {
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-gray-900">Erlaubte Dateitypen</label>
            <p class="text-xs text-gray-500 leading-tight">
              Im Regelset je Prüfregel definiert. Für diese Dateitypen wird nichts extrahiert – der
              Knoten gibt die Datei selbst weiter (Ausgänge: Projektdateien, Datei-URI, Dateiname).
            </p>
            <div class="flex flex-wrap gap-1.5">
              @for (typ of eingangDateitypen; track typ) {
                <span class="text-xs px-2 py-1 rounded font-medium bg-blue-100 text-blue-700">{{ typ }}</span>
              }
            </div>
          </div>
        }

        <!-- eingangsparameter: set-verwaltete Parameter (read-only) + Sichtbarkeits-Toggle -->
        @if (isEingangsparameterNode && !isEingangDateiModus) {
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-gray-900">Eingabeparameter</label>
            <p class="text-xs text-gray-500 leading-tight">
              Im Regelset definiert. Auge = in dieser Regel genutzt (extrahiert).
            </p>
            @if (eingangParameters.length === 0) {
              <div class="text-sm text-gray-400 py-2">Keine Eingabeparameter im Regelset definiert.</div>
            } @else {
              @for (param of eingangParameters; track param.id) {
                <div class="flex items-center gap-2 h-[38px] px-3 bg-gray-50 border border-gray-300 rounded-lg"
                     [class.opacity-50]="!isEingangParamActive(param)">
                  <span class="text-sm text-gray-900 truncate flex-1 min-w-0">{{ param.name || 'Unbenannt' }}</span>
                  <span class="text-xs text-gray-400 shrink-0">{{ param.typ || 'Text' }}</span>
                  @if (isEingangParamIdentifikator(param)) {
                    <span
                      class="text-xs px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700 shrink-0">Identifikator</span>
                  } @else {
                    <button (click)="toggleEingangParam(param)"
                            [attr.aria-label]="isEingangParamActive(param) ? 'Parameter verstecken' : 'Parameter nutzen'"
                            [class.text-gray-700]="isEingangParamActive(param)"
                            [class.text-gray-300]="!isEingangParamActive(param)"
                            class="shrink-0 w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 transition-colors">
                      <fa-icon [icon]="isEingangParamActive(param) ? faEye : faEyeSlash"></fa-icon>
                    </button>
                  }
                </div>
              }
            }
          </div>
        }
      }

      <!-- Formel: je Var-Slot eine eigene Bindung (die Position trägt die Bedeutung) -->
      @if (isFormelModule) {
        @for (row of formelVariableRows(); track row.index) {
          <app-workflow-value-binding
            (cleared)="onFormelSlot(row.index, null)"
            (literalChange)="onFormelSlot(row.index, $event)"
            (referenceChange)="onFormelSlot(row.index, $event)"
            [binding]="row.binding"
            [candidates]="bindingCandidates"
            [label]="row.label"
            [readOnly]="readOnly"
            [schema]="formelSlotSchema"
            [source]="row.source">
          </app-workflow-value-binding>
        }
      }
    </div>
  </div>
</div>

<!-- Rendering eines einzelnen Eingangs-Ports (wiederverwendet in beiden Layout-Pfaden) -->
<ng-template #inputFieldTpl let-field="field">
  @switch (inputKind(field)) {

    @case ('rulelist') {
      <!-- Prüfregel-Liste (CHECKER): Ruleset-Picker -->
      <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-gray-900">{{ field.label }}</label>
        @for (added of getAddedRulesets(field); track added.id) {
          <div class="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-lg">
            <span class="text-sm font-medium text-gray-800 flex-1 truncate">{{ added.name }}</span>
            <span
              [class]="RULESET_TYPE_CONFIG[added.type].bgClass + ' ' + RULESET_TYPE_CONFIG[added.type].textClass"
              class="text-xs px-1.5 py-0.5 rounded font-medium">
              {{ RULESET_TYPE_CONFIG[added.type].label }}
            </span>
            <button (click)="onRemoveRulesetFromList(field, added.id)"
                    class="text-gray-400 hover:text-red transition-colors">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
              </svg>
            </button>
          </div>
        }
        <input
          [(ngModel)]="rulesetSearchQuery"
          class="w-full h-[38px] px-3 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg
                 focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-colors"
          placeholder="myCheck suchen…"
          type="text"/>
        @if (loadingRulesets) {
          <span class="text-xs text-gray-400 py-2 text-center">Lade Prüfregeln…</span>
        } @else if (filteredRulesets.length === 0) {
          <span class="text-xs text-gray-400 py-2 text-center">Keine Prüfregeln vorhanden</span>
        } @else {
          <div class="flex flex-col gap-1.5 max-h-[180px] overflow-y-auto">
            @for (ruleset of filteredRulesets; track ruleset.id) {
              <div (click)="onAddRulesetToList(field, ruleset)"
                   class="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-lg
                          hover:border-gray-400 transition-colors cursor-pointer group">
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-sm font-medium text-gray-800 truncate">{{ ruleset.name }}</span>
                  <div class="flex items-center gap-2 mt-0.5">
                    <span
                      [class]="RULESET_TYPE_CONFIG[ruleset.type].bgClass + ' ' + RULESET_TYPE_CONFIG[ruleset.type].textClass"
                      class="text-xs px-1.5 py-0.5 rounded font-medium">
                      {{ RULESET_TYPE_CONFIG[ruleset.type].label }}
                    </span>
                    <span class="text-xs text-gray-400">{{ ruleset.ruleCount }} Regeln</span>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
    @case ('widget') {
      <!-- Eingang mit eigenem Widget (x-widget), z.B. KI-Kontext: Dateiliste mit Auswahl-Popup.
           Auch hier ist eine Bindung an einen Vorgängerwert möglich. -->
      <app-workflow-value-binding
        (cleared)="onInputCleared(field)"
        (literalChange)="onInputLiteral(field, $event)"
        (referenceChange)="onInputReference(field, $event)"
        [binding]="field.binding"
        [candidates]="bindingCandidates"
        [description]="field.schemaProp?.description ?? ''"
        [label]="field.label"
        [readOnly]="readOnly"
        [schema]="resolvedInputProp(field)"
        [source]="field.source">
      </app-workflow-value-binding>
    }
    @case ('object') {
      <!-- Objekt-Eingang: ebenfalls über eine Bindung. Die {Platzhalter}-Auflösung braucht ihn nach
           dem impliziten Flow-Kontext nicht mehr, ein bestehender Wert bleibt aber sichtbar. -->
      <app-workflow-value-binding
        (cleared)="onInputCleared(field)"
        (literalChange)="onInputLiteral(field, $event)"
        (referenceChange)="onInputReference(field, $event)"
        [binding]="field.binding"
        [candidates]="bindingCandidates"
        [description]="field.schemaProp?.description ?? ''"
        [label]="field.label"
        [readOnly]="readOnly"
        [schema]="resolvedInputProp(field)"
        [source]="field.source">
      </app-workflow-value-binding>
    }
    @default {
      <!--
        Skalarer Eingang: Festwert eintragen ODER einen Wert aus einem vorherigen Schritt wählen.
        Beides landet in `inputs[schemaKey]` — ein Festwert direkt, eine Auswahl als
        `${node.outputs.key}`. Datenkanten gibt es dafür nicht mehr.
      -->
      <app-workflow-value-binding
        (cleared)="onInputCleared(field)"
        (literalChange)="onInputLiteral(field, $event)"
        (referenceChange)="onInputReference(field, $event)"
        [binding]="field.binding"
        [candidates]="bindingCandidates"
        [hint]="inputHint(field)"
        [label]="field.label"
        [readOnly]="readOnly"
        [schema]="resolvedInputProp(field)"
        [source]="field.source">
      </app-workflow-value-binding>
    }
  }
</ng-template>
